import {RoomState, RoomCreationPhase} from '../../../../../shared/RoomState'
import {SocketLobbySend} from '../../../../shared/scripts/socket/SocketLobbySend'
import {Sound} from '../../../../shared/scripts/Sound'
import {CA} from '../../../../shared/scripts/analytics/Analytics'
import {UIRoomCenterCheckboxController} from './UIRoomCenterCheckboxController'
import {UIRoomCenterSettingsModeController} from './UIRoomCenterSettingsModeController'
import {UIRoomCenterSettingsSpeedController, UIRoomCenterSettingSpeedType} from './UIRoomCenterSettingsSpeedController'
import {UIRoomCenterSettingsMapController} from './UIRoomCenterSettingsMapController'
import {UIRoomCenterSettingsDiceController} from './UIRoomCenterSettingsDiceController'
import {GameModeDataController} from '@colonist/shared-types'
import {UIRoomCenterShareLinkController} from './UIRoomCenterShareLinkController'
import {uiManager} from '../../EntryHomepage'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {socket} from '../../../../shared/scripts/EntryShared'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {AnalyticEventValue, AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {ModeSettingEnum, RoomSettingConstants} from '@colonist/game-base-types'
import {UIRoomCenterInfoMessageController} from './UIRoomCenterInfoMessageController'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState'
import {UIRoomWarningController, WarningType} from '../../../../shared/scripts/ui_html/room_warning/UIRoomWarningController'
import {UIRoomCenterSeedOrderSelectionController} from './play_order_selection/UIRoomCenterSeedOrderSelectionController'
import {setElementInnerHTML} from '../../../../shared/scripts/ui_utils/DomUtils'
import {TranslationObject} from '@colonist/i18n'
import {RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {DiscordInstance} from '../../discord/DiscordInstance.ts'
import {ViewPopupBecomeMemberGameplay} from '../../../../shared/scripts/ui_popup/ViewPopupBecomeMemberGameplay'
import {SDKRequest} from '../../../../../shared/DiscordActivityTypes'
import {ColonistMembershipType} from '@colonist/types'
import {UIRoomSliderController} from '../../../../shared/scripts/ui_html/room/UIRoomSliderController.ts'

export class UIWRoomCenterController {
    private readonly roomCenterContainer: HTMLDivElement
    private readonly scrollContainer: HTMLDivElement
    private readonly roomStartActionsContainer: HTMLDivElement

    private readonly maxPlayersSlider: UIRoomSliderController
    private readonly cardDiscardLimitSlider: UIRoomSliderController
    private readonly victoryPointsSlider: UIRoomSliderController

    private readonly checkBoxReady: HTMLInputElement
    private readonly startButton: HTMLLinkElement
    private readonly startCustomOrderSelectionButton: HTMLLinkElement
    private readonly imReadySpan: HTMLSpanElement
    private readonly exitButton: HTMLLinkElement
    private readonly becomeAMemberButton: HTMLLinkElement | null
    private readonly karmaFilterMembershipIcon: HTMLImageElement
    private readonly checkboxController: UIRoomCenterCheckboxController
    private readonly modeSettingsController: UIRoomCenterSettingsModeController
    private readonly mapSettingsController: UIRoomCenterSettingsMapController
    private readonly diceSettingsController: UIRoomCenterSettingsDiceController
    private readonly gameSpeedSettingsController: UIRoomCenterSettingsSpeedController
    private readonly botSpeedSettingsController: UIRoomCenterSettingsSpeedController
    private readonly shareLinkController: UIRoomCenterShareLinkController
    private readonly seedOrderSelectionController: UIRoomCenterSeedOrderSelectionController
    readonly infoMessageController: UIRoomCenterInfoMessageController

    private roomState: RoomState

    private readonly DEFAULT_SLIDER_STEP = 1

    constructor() {
        this.roomCenterContainer = document.getElementById('room_center_mid') as HTMLDivElement
        this.scrollContainer = document.getElementById('room_center_scroll') as HTMLDivElement
        this.roomStartActionsContainer = document.getElementById('room_start_actions') as HTMLDivElement

        this.maxPlayersSlider = new UIRoomSliderController(this.scrollContainer,
            {key: 'strings:roomPage.options.maxPlayers.title'},
            RoomSettingConstants.DEFAULT_PLAYER_COUNT,
            RoomSettingConstants.MIN_PLAYER_COUNT,
            RoomSettingConstants.MAX_PLAYER_COUNT,
            this.DEFAULT_SLIDER_STEP,
            (maxPlayers) => SocketLobbySend.clickedRoomSettingChangeMaxPlayers(maxPlayers))

        this.victoryPointsSlider = new UIRoomSliderController(this.scrollContainer,
            {key: 'strings:roomPage.options.vpToWin.title'},
            RoomSettingConstants.DEFAULT_VP_TO_WIN,
            RoomSettingConstants.MIN_VP_TO_WIN,
            RoomSettingConstants.DEFAULT_VP_TO_WIN,
            this.DEFAULT_SLIDER_STEP,
            (victoryPoints) => SocketLobbySend.clickedRoomSettingChangeVictoryPointsToWin(victoryPoints))

        this.cardDiscardLimitSlider = new UIRoomSliderController(this.scrollContainer,
            {key: 'strings:roomPage.options.cardDiscardLimit.title'},
            RoomSettingConstants.DEFAULT_CARD_DISCARD_LIMIT,
            RoomSettingConstants.MIN_CARD_DISCARD_LIMIT,
            RoomSettingConstants.MAX_CARD_DISCARD_LIMIT,
            this.DEFAULT_SLIDER_STEP,
            (cardDiscard) => SocketLobbySend.clickedRoomSettingChangeCardDiscardLimit(cardDiscard))

        this.checkBoxReady = document.getElementById('room_center_checkbox_ready') as HTMLInputElement
        this.startButton = document.getElementById('room_center_start_button') as HTMLLinkElement
        this.startCustomOrderSelectionButton = document.getElementById('room_center_start_order_selection_button') as HTMLLinkElement
        this.imReadySpan = document.getElementById('room_center_im_ready') as HTMLSpanElement
        this.exitButton = document.getElementById('room_center_exit_button') as HTMLLinkElement
        this.becomeAMemberButton = document.getElementById('room_center_become_member_button') as HTMLLinkElement | null
        this.karmaFilterMembershipIcon = document.getElementById('karma_filter_membership_icon') as HTMLImageElement
        this.checkboxController = new UIRoomCenterCheckboxController()
        this.modeSettingsController = new UIRoomCenterSettingsModeController()
        this.mapSettingsController = new UIRoomCenterSettingsMapController()
        this.diceSettingsController = new UIRoomCenterSettingsDiceController()
        this.gameSpeedSettingsController = new UIRoomCenterSettingsSpeedController(UIRoomCenterSettingSpeedType.GameSpeed)
        this.botSpeedSettingsController = new UIRoomCenterSettingsSpeedController(UIRoomCenterSettingSpeedType.BotSpeed)
        this.shareLinkController = new UIRoomCenterShareLinkController()
        this.seedOrderSelectionController = new UIRoomCenterSeedOrderSelectionController(this.scrollContainer)

        this.infoMessageController = new UIRoomCenterInfoMessageController()
    }

    async update(oldRoomState: RoomState | undefined, newRoomState: RoomState) {
        this.roomState = newRoomState
        this.checkboxController.update(newRoomState)

        this.updateTitle()
        this.updateScrollContainerContent()
        this.updateVisibleFooterContent(newRoomState)
        for(const sessionState of newRoomState.getSessions()) {
            if(sessionState.userSessionId == socket.controller.session.id) this.checkBoxReady.checked = sessionState.isReadyToPlay
        }

        await this.modeSettingsController.update(newRoomState)
        await this.mapSettingsController.update(newRoomState)
        this.diceSettingsController.update(newRoomState)
        this.gameSpeedSettingsController.update(newRoomState)
        this.botSpeedSettingsController.update(newRoomState)
        this.updateMaxPlayerCountSliderTicks()

        this.updateInteractabilityOfElements(newRoomState)

        const isGuest = UIWRoomCenterController.isGuest(newRoomState)
        this.maxPlayersSlider.setValue(newRoomState.gameSetting.maxPlayers, isGuest)
        this.cardDiscardLimitSlider.setValue(newRoomState.gameSetting.cardDiscardLimit, isGuest)

        if(oldRoomState?.victoryPointsMaxAllowed != newRoomState.victoryPointsMaxAllowed) this.victoryPointsSlider.updateSliderTicks(newRoomState.victoryPointsMaxAllowed)
        this.victoryPointsSlider.setWarningThreshold(newRoomState.victoryPointsRecommendedLimit)
        this.victoryPointsSlider.setValue(newRoomState.gameSetting.victoryPointsToWin, isGuest)
    }

    async load(roomState: RoomState) {
        this.roomState = roomState

        this.shareLinkController.load(roomState)
        this.checkboxController.load(roomState)
        await this.modeSettingsController.load(roomState)
        await this.mapSettingsController.load(roomState)
        this.diceSettingsController.load(roomState)
        this.gameSpeedSettingsController.load(roomState)
        this.botSpeedSettingsController.load(roomState)

        this.loadFooterContent(roomState)

        this.exitButton.onclick = () => {
            Sound.playSound(Sound.SoundType.Click)
            uiManager.room.leaveRoom()
        }
        this.karmaFilterMembershipIcon.onclick = () => {
            UIWRoomCenterController.sendKarmaFilterIconAnalyticEvent()
        }
        if(this.becomeAMemberButton) {
            this.becomeAMemberButton.onclick = () => {
                if(UIUtilsEjsVariables.isUserOnDiscord) {
                    const checkAction = () => {
                        void DiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.PurchaseMembership, ColonistMembershipType.PremiumMonthly)
                    }
                    new ViewPopupBecomeMemberGameplay(checkAction, AnalyticEventValue.PopupMembershipIncentiveDiscordGeneral)
                }
                UIWRoomCenterController.sendBecomeAMemberButtonAnalyticEvent()
            }
        }

        this.updateInteractabilityOfElements(roomState)

        await this.update(undefined, roomState)
        this.hideSettingsIfBeginnerMode(roomState.gameSetting.modeSetting)
    }

    private loadFooterContent(roomState: RoomState) {
        this.checkBoxReady.onclick = (e) => {
            e.preventDefault()
            UIRoomWarningController.runWithWarning((userAgreed?: boolean) => {
                if(userAgreed) this.checkBoxReady.checked = true
                Sound.playSound(Sound.SoundType.Click)
                SocketLobbySend.clickedRoomReadyToStart(this.checkBoxReady.checked)
            }, WarningType.ReadyUpVPExceededRecommendedLimit, this.roomState)
        }

        this.startButton.onclick = () => UIRoomWarningController.runWithWarning(() => {
            Sound.playSound(Sound.SoundType.Click)
            if(this.roomState != undefined) {
                if(this.roomState.isMultiplayer()) CA.sendGoogleAnalyticsEvent(CA.Category.Funnel, CA.Label.FunnelStartedMultiplayerRoom)
            }
            SocketLobbySend.clickedRoomStartGame()
        }, WarningType.StartGameVPExceededRecommendedLimit, this.roomState)

        this.startCustomOrderSelectionButton.onclick = () => {
            Sound.playSound(Sound.SoundType.Click)
            SocketLobbySend.clickedRoomStartCustomOrderSelection()
        }

        this.setDefaultFooterContentVisibility(roomState)
    }

    private setDefaultFooterContentVisibility(roomState: RoomState) {
        this.startButton.hidden = roomState.gameSetting.playOrderSelectionActive
        this.startCustomOrderSelectionButton.hidden = !roomState.gameSetting.playOrderSelectionActive
    }

    private updateInteractabilityOfElements(newRoomState: RoomState) {
        const isGuest = UIWRoomCenterController.isGuest(newRoomState)
        const isMember = UIWRoomCenterController.isMember()
        if(this.startButton.parentElement) this.startButton.parentElement.hidden = isGuest
        if(this.startCustomOrderSelectionButton.parentElement) this.startCustomOrderSelectionButton.parentElement.hidden = isGuest
        if(this.becomeAMemberButton?.parentElement) this.becomeAMemberButton.parentElement.hidden = isMember
        this.checkboxController.updateInteractabilityOfElements(newRoomState)
        this.modeSettingsController.updateInteractabilityOfElements(isGuest)
        this.mapSettingsController.updateInteractabilityOfElements(isGuest)
        this.diceSettingsController.updateInteractabilityOfElements(isGuest)
        this.gameSpeedSettingsController.updateInteractabilityOfElements(newRoomState)
        this.botSpeedSettingsController.updateInteractabilityOfElements(newRoomState)
    }

    private static isGuest(roomState: RoomState): boolean {
        return socket.controller.session.id != roomState.hostSession.userSessionId
    }

    private static isMember(): boolean {
        return ClientUserState.userState?.items.hasMembership() ?? false
    }

    private updateMaxPlayerCountSliderTicks() {
        const newMaxPlayerCount = GameModeDataController.getMaxAllowablePlayerCountForMode(this.roomState.gameSetting.modeSetting)
        if(newMaxPlayerCount == this.maxPlayersSlider.max) return
        this.maxPlayersSlider.updateSliderTicks(newMaxPlayerCount)
    }

    private static sendKarmaFilterIconAnalyticEvent() {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: AnalyticEventValue.RoomKarmaFilterIcon,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    private static sendBecomeAMemberButtonAnalyticEvent() {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: AnalyticEventValue.RoomBecomeAMemberButton,
        }
        void CA.sendAnalyticEvent(eventData)
    }

    private updateTitle() {
        const gameSettingsTitle = document.getElementById('room_center_game_settings_title') as HTMLDivElement
        const titleShouldBeSeedOrderSelection = this.roomState.creationPhase == RoomCreationPhase.SeedOrderSelection && gameSettingsTitle != null
        const newTitle: TranslationObject = titleShouldBeSeedOrderSelection ? {key: 'strings:roomPage.orderSelection.seedOrderSelection.title'} : {key: 'strings:roomPage.gameSettings'}
        setElementInnerHTML(gameSettingsTitle, newTitle)
    }

    private updateScrollContainerContent() {
        const shouldHideDefaultScrollContainerContent = this.roomState.creationPhase == RoomCreationPhase.SeedOrderSelection
        for(const child of this.scrollContainer.children) (child as HTMLElement).hidden = shouldHideDefaultScrollContainerContent
        this.seedOrderSelectionController.setScrollContainerContentVisibility(shouldHideDefaultScrollContainerContent)
    }

    private updateVisibleFooterContent(roomState: RoomState) {
        this.setDefaultFooterContentVisibility(roomState)
        const footer = document.getElementById('room_center_footer') as HTMLDivElement
        const shouldHideDefaultFooterContent = this.roomState.creationPhase == RoomCreationPhase.SeedOrderSelection && footer != null
        for(const child of footer.children) (child as HTMLElement).hidden = shouldHideDefaultFooterContent
        this.seedOrderSelectionController.setFooterContentVisibility(shouldHideDefaultFooterContent)
    }

    private hideSettingsIfBeginnerMode(modeSetting: ModeSettingEnum) {
        const settingsOverlay = document.getElementById('room-center-settings-overlay') as HTMLDivElement
        const shouldHideSettings = modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        if(settingsOverlay == null) return
        if(shouldHideSettings) {
            this.scrollContainer.style.overflowY = 'hidden'
            settingsOverlay.style.display = 'flex'
            return
        }
        this.scrollContainer.style.overflowY = 'scroll'
        settingsOverlay.style.display = 'none'
    }

    updateSeedOrderSelection(newRoomState: RoomState, data: RoomSeedOrderSelectionData) {
        const footer = document.getElementById('room_center_footer') as HTMLDivElement
        this.seedOrderSelectionController.updateSeedOrderSelection(newRoomState, footer, data)
    }
}
